import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

export default function BestFindsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL;

  return (
    <main>
      <Navbar />

      <article>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "Best Spreadsheet Finds 2026 — Top CSSBuy Sheet Picks Across All Categories",
              "description": "Our editors curate the absolute best spreadsheet finds of 2026 — top sneaker batches, must-cop hoodies, hidden gem accessories, and standout pieces from every CSSBuy spreadsheet category. Updated weekly with fresh community-vetted picks and detailed QC notes.",
              "author": { "@type": "Organization", "name": "CSSSpreadsheet.fun" },
              "publisher": { "@type": "Organization", "name": "CSSSpreadsheet.fun", "url": siteUrl },
              "datePublished": "2026-07-06",
              "dateModified": "2026-07-06",
              "url": `${siteUrl}/best-spreadsheet-finds-2026`,
              "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}/best-spreadsheet-finds-2026` }
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                { "@type": "Question", "name": "How often are the best spreadsheet finds updated?", "acceptedAnswer": { "@type": "Answer", "text": "Our curated picks are reviewed weekly. If a batch quality declines or a better alternative emerges, we update the listing immediately. Follow our homepage for daily new additions and community notes on trending finds." } },
                { "@type": "Question", "name": "Are these spreadsheet finds compatible with all agents?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Every spreadsheet find on this page links to the original Taobao or Weidian listing, which works with CSSBuy, KakoBuy, Superbuy, and Oopbuy. See our CSSBuy vs KakoBuy comparison to choose the right agent for your haul." } },
                { "@type": "Question", "name": "Can I request specific items to be reviewed?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. Submit your item requests through our main store at kaspreadsheet.com. Our community of spreadsheet hunters will source, QC, and review the best available batches within 48 hours." } }
              ]
            })
          }}
        />

        <header className="bg-foreground-950 px-6 md:px-10 lg:px-16 pt-24 pb-16 md:pt-32 md:pb-20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-foreground-400 text-xs tracking-[0.25em] uppercase font-light mb-6">
              Curated Picks 2026
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 font-semibold leading-tight mb-8">
              Best Spreadsheet Finds<br />
              of 2026: The Only<br />
              Picks That Matter
            </h1>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-foreground-900 text-sm font-semibold rounded-full hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Hub
              </a>
              <a
                href="/cssbuy-spreadsheet-guide-2026"
                className="inline-flex items-center gap-2 px-8 py-3.5 border border-background-50/30 text-background-50 text-sm font-semibold rounded-full hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                Shopping Guide
              </a>
            </div>
          </div>
        </header>

        <div className="flex flex-col lg:flex-row bg-background-50">
          <div className="w-full lg:w-2/5 px-6 md:px-10 lg:px-16 py-12 md:py-16 flex items-center">
            <div className="max-w-md">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-4">
                We Did the Scrolling So You Do Not Have To
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                The spreadsheet world moves fast — thousands of new links appear every week across dozens of spreadsheets. Our editorial team filters through the noise to find the 1% of <strong>spreadsheet finds</strong> that are genuinely worth your money and warehouse space. Every pick on this page has passed our triple-check system: multiple QC photo verifications, community consensus confirmation, and at least 30 days of sustained positive feedback. No hype picks, no paid placements — just the best stuff our community actually buys and loves.
              </p>
            </div>
          </div>
          <div className="w-full lg:w-3/5 h-[300px] lg:h-[420px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Curated%20editorial%20flat%20lay%20of%20premium%20fashion%20items%20including%20designer%20sneakers%2C%20a%20luxury%20hoodie%2C%20gold%20accessories%20and%20a%20leather%20bag%20arranged%20on%20a%20polished%20marble%20surface%2C%20soft%20natural%20window%20light%2C%20high%20end%20fashion%20editorial%20photography%2C%20sophisticated%20and%20aspirational%20composition%20with%20rich%20neutral%20tones&width=1400&height=900&seq=bestfinds-intro-v3&orientation=landscape"
              alt="Best Spreadsheet Finds 2026 Collection"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <section className="px-6 md:px-10 lg:px-16 py-12 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <p className="text-foreground-600 leading-relaxed mb-10">
              Finding the <strong>best spreadsheet finds</strong> is not about chasing the flashiest brands or the lowest prices. It is about identifying the items that deliver the highest satisfaction-to-effort ratio — pieces that arrive looking exactly like the QC photos, fit well without complicated size gymnastics, and hold up after months of wear. These are the finds our community reorders, recommends to friends, and keeps in their permanent rotation. If you are new to spreadsheet shopping, these picks are your safest and most rewarding entry points. For a step-by-step walkthrough of how the whole system works, read our <a href="/cssbuy-spreadsheet-guide-2026" className="text-primary-500 hover:text-primary-600 underline underline-offset-2 transition-colors">complete CSSBuy spreadsheet guide</a> first.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-8">
              The Best Spreadsheet Finds of 2026 by Category
            </h2>

            {[
              {
                category: 'Best Sneaker Find',
                item: 'Air Jordan 4 — Military Black (GX Batch)',
                price: '$48-55',
                verdict: 'The GX batch Jordan 4 Military Black is hands-down the most impressive spreadsheet sneaker of 2026. The shape accuracy, cage alignment, and heel tab positioning rival batches costing twice as much. Community consensus puts this at a 9.2/10 versus retail — the highest score we have ever recorded for a sub-$60 sneaker. The subtle colorway means it pairs with virtually any outfit, and GX batch consistency means you can order with confidence every time.',
                icon: 'ri-footprint-line',
                color: 'bg-secondary-100',
                iconColor: 'text-secondary-600',
              },
              {
                category: 'Best Hoodie Find',
                item: 'Essentials Fear of God — Core Hoodie (Independent Batch)',
                price: '$32-40',
                verdict: 'Independent batch Essentials hoodies have been a spreadsheet staple for two years, and the 2026 batch update is the best yet. The 420GSM French terry cotton feels substantial without being stiff, the 3D silicone applique lettering is crisp, and the oversized fit measurements now match retail within a 2cm tolerance across all sizes. At under $40, this hoodie outperforms $100+ retail alternatives. The cream and charcoal colorways are the most versatile picks.',
                icon: 'ri-t-shirt-line',
                iconColor: 'text-accent-600',
                color: 'bg-accent-100',
              },
              {
                category: 'Best Accessory Find',
                item: 'Vivienne Westwood — Pearl Choker Necklace',
                price: '$12-18',
                verdict: 'This is the definition of a spreadsheet hidden gem. For the price of a fast-food meal, you get a surprisingly well-made pearl choker with the signature orb pendant. The pearls have decent weight and luster, the chain does not tarnish with normal wear, and the clasp mechanism is solid. It has become the unofficial starter accessory for spreadsheet shoppers — easy to style, hard to mess up, and an instant outfit elevator. Grab this, a basic tee, and some clean sneakers for a complete look under $60 total.',
                icon: 'ri-vip-diamond-line',
                iconColor: 'text-primary-600',
                color: 'bg-primary-100',
              },
              {
                category: 'Best Bag Find',
                item: 'Arc\'teryx — Mantis 2 Waistpack',
                price: '$15-22',
                verdict: 'The spreadsheet techwear category has exploded in 2026, and this Arc\'teryx Mantis waistpack is the standout piece. The material is water-resistant ripstop nylon that feels nearly identical to retail, the zippers are smooth YKK-style pulls, and the internal organization pockets are faithfully reproduced. It works as a crossbody sling, a waist pack, or strapped to a larger backpack. For the outdoor-aesthetic crowd and anyone who appreciates functional design, this is an absolute must-cop.',
                icon: 'ri-briefcase-line',
                iconColor: 'text-secondary-600',
                color: 'bg-secondary-100',
              },
              {
                category: 'Best T-Shirt Find',
                item: 'Heavyweight Blank Tee 320GSM — Box Fit',
                price: '$8-12',
                verdict: 'The foundation of every good outfit is a great fitting t-shirt, and this heavyweight blank tee is the spreadsheet community most-bought item of the year. The 320GSM cotton is thick enough to hold structure without being uncomfortably heavy. The boxy cropped fit works perfectly with wide-leg pants or cargo joggers. Available in 14 colors with consistent sizing across all batches. At $8-12, smart spreadsheet shoppers buy these in packs of five. The washed black, army green, and off-white are the most popular colorways.',
                icon: 'ri-shirt-line',
                iconColor: 'text-accent-600',
                color: 'bg-accent-100',
              },
              {
                category: 'Best Statement Piece',
                item: 'Gallery Dept. — Flared Sweatpants',
                price: '$35-45',
                verdict: 'If you want one piece that transforms your entire wardrobe, these Gallery Dept. flared sweatpants are it. The heavyweight cotton fleece drapes beautifully, the paint splatter details are unique to each pair, and the flared leg opening creates that coveted stacked-over-sneakers silhouette. These are not subtle — they make a statement — but the quality and construction are good enough that they do not feel like fast fashion. Pair with the heavyweight blank tee above and any clean sneaker for an effortlessly cool outfit.',
                icon: 'ri-brush-line',
                iconColor: 'text-primary-600',
                color: 'bg-primary-100',
              },
            ].map((item, idx) => (
              <div key={idx} className="mb-8 bg-background-100 rounded-2xl p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center ${item.color} rounded-lg`}>
                    <i className={`${item.icon} ${item.iconColor} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-foreground-400 text-xs uppercase tracking-wider font-medium">{item.category}</p>
                    <h3 className="text-foreground-900 font-heading text-xl font-semibold">{item.item}</h3>
                    <span className="text-accent-600 text-sm font-semibold">{item.price}</span>
                  </div>
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed">{item.verdict}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-6 md:px-10 lg:px-16 py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-6">
              How We Select the Best Spreadsheet Finds
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              Our selection process is rigorous and community-driven. Every item that makes it onto this list passes through three gates: first, the item must have at least 20 verified QC photo posts across Reddit and Discord showing consistent quality. Second, it must maintain a community satisfaction rating above 8/10 over a minimum 30-day window — this filters out batches that start strong but decline. Third, the seller must have a reliable track record with fast shipping to agent warehouses and responsive communication for size swaps or issues.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-6">
              We also prioritize items that offer <strong>spreadsheet finds</strong> value — meaning the quality-to-price ratio is exceptional, not just the absolute lowest price. A $45 item that looks and feels like a $300 retail piece is far more interesting to us than a $10 item that looks like it cost $10. This is the philosophy behind every pick on this page. For the budget-focused approach, check out our <a href="/cheap-spreadsheet-sneakers-2026" className="text-primary-500 hover:text-primary-600 underline underline-offset-2 transition-colors">cheap spreadsheet sneakers guide</a>.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-6 mt-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-heading text-lg text-foreground-900 font-semibold mb-2">
                  How often are the best spreadsheet finds updated?
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Our curated picks are reviewed weekly. If a batch quality declines or a better alternative emerges, we update the listing immediately. Follow <a href="/" className="text-primary-500 hover:text-primary-600 underline underline-offset-2 transition-colors">our homepage</a> for daily new additions and community notes on trending finds.
                </p>
              </div>
              <div>
                <h3 className="font-heading text-lg text-foreground-900 font-semibold mb-2">
                  Are these spreadsheet finds compatible with all agents?
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Yes. Every <strong>spreadsheet find</strong> on this page links to the original Taobao or Weidian listing, which works with CSSBuy, KakoBuy, Superbuy, and Oopbuy. See our <a href="/cssbuy-vs-kakobuy" className="text-primary-500 hover:text-primary-600 underline underline-offset-2 transition-colors">CSSBuy vs KakoBuy comparison</a> to choose the right agent for your haul.
                </p>
              </div>
              <div>
                <h3 className="font-heading text-lg text-foreground-900 font-semibold mb-2">
                  Can I request specific items to be reviewed?
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Absolutely. Submit your item requests through our <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline underline-offset-2 transition-colors">main store at kaspreadsheet.com</a>. Our community of spreadsheet hunters will source, QC, and review the best available batches within 48 hours.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 md:px-10 lg:px-16 py-12 md:py-16 bg-background-100">
          <div className="max-w-5xl mx-auto text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-2">
              Continue Exploring
            </h2>
            <p className="text-foreground-500 text-sm">More curated spreadsheet content</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <a href="/cssbuy-spreadsheet-guide-2026" className="group bg-background-50 rounded-xl p-6 hover:scale-[1.02] transition-all duration-300 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg mb-4">
                <i className="ri-book-open-line text-secondary-600 text-lg"></i>
              </div>
              <h3 className="text-foreground-900 font-semibold text-sm mb-2">Spreadsheet Shopping Guide</h3>
              <p className="text-foreground-500 text-xs">Everything you need to know to get started</p>
            </a>
            <a href="/cheap-spreadsheet-sneakers-2026" className="group bg-background-50 rounded-xl p-6 hover:scale-[1.02] transition-all duration-300 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg mb-4">
                <i className="ri-footprint-line text-accent-600 text-lg"></i>
              </div>
              <h3 className="text-foreground-900 font-semibold text-sm mb-2">Budget Sneakers Guide</h3>
              <p className="text-foreground-500 text-xs">Top 8 affordable spreadsheet sneaker picks</p>
            </a>
            <a href="/cssbuy-vs-kakobuy" className="group bg-background-50 rounded-xl p-6 hover:scale-[1.02] transition-all duration-300 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className="ri-scales-line text-primary-600 text-lg"></i>
              </div>
              <h3 className="text-foreground-900 font-semibold text-sm mb-2">CSSBuy vs KakoBuy</h3>
              <p className="text-foreground-500 text-xs">In-depth agent comparison for spreadsheet shoppers</p>
            </a>
          </div>
        </section>

        <div className="flex flex-col lg:flex-row min-h-[400px] md:min-h-[460px]">
          <div className="w-full lg:w-1/2 relative h-[250px] lg:h-auto overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20a%20perfectly%20styled%20complete%20outfit%20including%20sneakers%2C%20folded%20jeans%2C%20a%20hoodie%20and%20accessories%20on%20a%20light%20oak%20wooden%20floor%2C%20bright%20natural%20daylight%20streaming%20in%2C%20aspirational%20lifestyle%20photography%2C%20fresh%20modern%20composition%20with%20warm%20natural%20tones&width=1200&height=900&seq=bestfinds-cta-v3&orientation=landscape"
              alt="Your Best Spreadsheet Wardrobe"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="w-full lg:w-1/2 flex items-center justify-center bg-background-50 px-8 py-12 lg:p-16">
            <div className="max-w-md text-center">
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 font-bold leading-tight mb-4">
                Build Your Best<br />Spreadsheet Wardrobe
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8 max-w-sm mx-auto">
                Every item on this page has been community-tested and approved. Start with the essentials — a great tee, a solid hoodie, and clean sneakers — then expand into statement pieces as you build confidence. The <strong>best spreadsheet finds</strong> are just a few clicks away.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Browse All Spreadsheet Finds
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}